import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import { useApi } from '../api/useApi';
import { useDiscoveryState } from '../api/useDiscoveryState';
import AsyncSection from '../components/AsyncSection';
import LibraryFilter from '../components/media/LibraryFilter';
import CatalogueBrowser, { CatalogueSwitch } from '../components/media/CatalogueBrowser';
import MediaGrid from '../components/media/MediaGrid';

/// Every film on the server, from every library. Which library one came from is not something
/// a viewer should have to think about, so it is not on this screen.
export default function MoviesPage() {
  const { t } = useTranslation();
  const [library, setLibrary] = useState<string>();
  const discovery = useDiscoveryState();
  const [showingCatalogue, setShowingCatalogue] = useState(true);

  // Where the server was told to show the catalogue as the library, this screen is the
  // catalogue narrowed to what it is about - with a way back to what this server actually
  // holds, because showing the world and hiding somebody's own shelf is not the trade.
  //
  // Where everything plays on demand there is no such way back to offer: nothing is "not here",
  // so the catalogue simply is the library and the switch would be a choice between two names
  // for the same thing.
  const onDemand = Boolean(discovery.playsOnDemand);
  const catalogue = onDemand || (discovery.catalogueInLibrary && showingCatalogue);
  const movies = useApi(
    ['items', 'Movie', library ?? 'all'],
    () => api.items({ kind: 'Movie', libraryId: library, take: 200 }),
    { enabled: !onDemand },
  );

  return (
    <Stack spacing={3}>
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}
      >
        <Typography variant="h2">{t('nav.movies')}</Typography>
        {!catalogue && movies.data && (
          <Typography color="text.secondary">{t('items.count', { count: movies.data.total })}</Typography>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {discovery.catalogueInLibrary && !onDemand && (
          <CatalogueSwitch showingCatalogue={showingCatalogue} onChange={setShowingCatalogue} />
        )}

        {!catalogue && <LibraryFilter serves={['Movies', 'Mixed']} value={library} onChange={setLibrary} />}
      </Stack>

      {catalogue ? (
        <CatalogueBrowser kind="Movie" />
      ) : (
        <AsyncSection loading={movies.loading} error={movies.error} onRetry={movies.reload}>
          <MediaGrid items={movies.data?.items ?? []} emptyKey="items.noMovies" />
        </AsyncSection>
      )}
    </Stack>
  );
}
